import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { ApolloError, MutationFunction } from "@apollo/client";
import { captureException } from "@sentry/browser";

import hotspringLogo from "@/assets/logo.svg";
import AuthCoverImage from "@/assets/AuthCover.png";

import { InputField } from "@/components/common/InputField/InputField";
import { Button } from "@/components/ui/button";
import { colors } from "@/theme/colors";

import {
  NewUserSignupMutation,
  NewUserSignupMutationVariables,
} from "@/__generated__/graphql";
import { LOGIN_URL } from "@/helpers/url";
import VendorAgreement, {
  AgreementType,
} from "@/components/common/VendorAgreement";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { useUserContext } from "@/contexts/UserContextProvider";

const signupSchema = z
  .object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    username: z.string().min(1, "Username is required"),
    password: z.string().min(12, "Password must be at least 12 characters"),
    password2: z
      .string()
      .min(12, "Confirm password must be at least 12 characters"),
    hasAcceptedTC: z
      .boolean()
      .refine((v) => v === true, "You must accept the terms and conditions"),
    hasAcceptedFreelancerAgreement: z.boolean(),
  })
  .refine((data) => data.password === data.password2, {
    message: "Passwords don't match",
    path: ["password2"],
  });

interface FormValues {
  firstName: string;
  lastName: string;
  username: string;
  password: string;
  password2: string;
  hasAcceptedTC: boolean;
  hasAcceptedFreelancerAgreement: boolean;
}

interface SignUpFormProps {
  signup: MutationFunction<
    NewUserSignupMutation,
    NewUserSignupMutationVariables
  >;
  token: string;
  company: string;
  inviterCompany: string;
  office: string;
  email: string;
  isSignUpAgreementDisabled: boolean;
  isArtistSignup: boolean;
  error: string;
}

const SignUpForm: React.FC<SignUpFormProps> = ({
  signup,
  token,
  isSignUpAgreementDisabled,
  isArtistSignup,
  error,
  office,
}) => {
  const { currentUser } = useUserContext();
  const [isSigningUp, setIsSigningUp] = useState(false);

  return (
    <div className="dark flex w-[100vw] h-[100vh] bg-background">
      <div className="w-[4vw] bg-[#000]" />

      <div className="w-[46vw] h-full">
        <img
          src={AuthCoverImage}
          alt="Cover image"
          className="h-full w-full object-cover"
        />
      </div>

      <div
        className="absolute top-0 left-[25vw] h-full z-11 pointer-events-none"
        style={{
          width: "25vw",
          background:
            "linear-gradient(270deg, #030A11 0%, rgba(3, 10, 17, 0) 100%)",
        }}
      />

      {error ? (
        <div className="text-red-500 w-[50vw] h-full bg-background flex flex-col items-center justify-center relative">
          {error}
        </div>
      ) : (
        <div className="w-[50vw] h-full bg-background flex flex-col items-center justify-center relative">
          <Formik<FormValues>
            initialValues={{
              firstName: "",
              lastName: "",
              username: "",
              password: "",
              password2: "",
              hasAcceptedTC: isSignUpAgreementDisabled,
              hasAcceptedFreelancerAgreement:
                isSignUpAgreementDisabled && !isArtistSignup,
            }}
            validationSchema={toFormikValidationSchema(signupSchema)}
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={async (
              values,
              { setStatus, setSubmitting, setFieldError },
            ) => {
              setIsSigningUp(true);

              try {
                if (currentUser) {
                  setStatus("You are logged in. Please log out to sign up.");
                  setSubmitting(false);
                  setIsSigningUp(false);
                  return;
                }

                const res = await signup({
                  variables: {
                    input: {
                      firstName: values.firstName,
                      lastName: values.lastName,
                      username: values.username,
                      password: values.password,
                      password2: values.password2,
                      token,
                    },
                    loginAfterSignup: false,
                  },
                });

                const signupResult = res?.data?.newUserSignup;
                if (signupResult?.success) {
                  window.location.href = LOGIN_URL;
                } else if (signupResult?.errors) {
                  signupResult?.errors?.map((err) => {
                    if (err) setFieldError(err.field, err.messages[0]);
                  });
                } else {
                  setStatus("Failed to sign up. Please try again.");
                }
              } catch (err) {
                captureException(err);
                if (err instanceof ApolloError) {
                  setStatus(err?.graphQLErrors?.[0]?.message);
                } else {
                  setStatus("Something went wrong");
                }
              } finally {
                setIsSigningUp(false);
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting, values, status, setFieldValue }) => (
              <Form className="w-full flex flex-col items-start gap-[12px] px-[22%]">
                <img src={hotspringLogo} alt="Hotspring Logo" />

                <div className="mb-0!">
                  <h1 className="text-xl text-white">Welcome!</h1>
                  <p className="text-secondary-foreground text-sm">
                    You've been invited to sign up as a user at {office}
                  </p>
                </div>

                {status && <p className="text-sm text-[#F04438]">{status}</p>}

                <div className="w-full flex flex-col gap-[24px]">
                  <Field
                    name="firstName"
                    placeholder="First Name"
                    component={InputField}
                    labelClassName="!text-white"
                    inputClassName="!text-white h-10 !border-border !bg-primary autofill"
                    iconClassName=""
                  />

                  <Field
                    name="lastName"
                    placeholder="Last Name"
                    component={InputField}
                    labelClassName="!text-white"
                    inputClassName="!text-white h-10 !border-border !bg-primary autofill"
                    iconClassName=""
                  />

                  <Field
                    name="username"
                    placeholder="Username"
                    component={InputField}
                    labelClassName="!text-white"
                    inputClassName="!text-white h-10 !border-border !bg-primary autofill"
                    iconClassName=""
                  />

                  <Field
                    name="password"
                    type="password"
                    placeholder="Password"
                    component={InputField}
                    labelClassName="!text-white"
                    inputClassName="!text-white h-10 !border-border !bg-primary autofill"
                    iconClassName="right-10"
                  />

                  <Field
                    name="password2"
                    type="password"
                    placeholder="Confirm Password"
                    component={InputField}
                    labelClassName="!text-white"
                    inputClassName="!text-white h-10 !border-border !bg-primary"
                    iconClassName="right-10"
                  />

                  {!isSignUpAgreementDisabled && (
                    <VendorAgreement
                      agreementType={AgreementType.PlatformUserAgreement}
                    >
                      <Checkbox
                        checked={values.hasAcceptedTC}
                        onCheckedChange={() => {
                          setFieldValue("hasAcceptedTC", !values.hasAcceptedTC);
                        }}
                        className={cn("border-none")}
                      />
                    </VendorAgreement>
                  )}

                  {isArtistSignup && !isSignUpAgreementDisabled && (
                    <VendorAgreement
                      agreementType={AgreementType.SubcontractorAgreement}
                    >
                      <Checkbox
                        checked={values.hasAcceptedFreelancerAgreement}
                        onCheckedChange={() => {
                          setFieldValue(
                            "hasAcceptedFreelancerAgreement",
                            !values.hasAcceptedFreelancerAgreement,
                          );
                        }}
                        className={cn("border-none")}
                      />
                    </VendorAgreement>
                  )}
                </div>

                <Button
                  type="submit"
                  style={{ background: colors.button }}
                  className="w-full text-white p-3 rounded !border-white hover:opacity-80 transition disabled:opacity-50 mt-5"
                  disabled={
                    isSubmitting ||
                    isSigningUp ||
                    !values.hasAcceptedTC ||
                    (isArtistSignup && !values.hasAcceptedFreelancerAgreement)
                  }
                >
                  {isSubmitting || isSigningUp ? "Signing up..." : "Sign Up"}
                </Button>
              </Form>
            )}
          </Formik>
        </div>
      )}
    </div>
  );
};

export default SignUpForm;
